import React, { useState } from 'react';
import { Calendar as CalendarIcon, RefreshCw, Tag, AlertCircle, CheckCircle2, XCircle, Clock, ArrowRight } from 'lucide-react';
import { Button } from '../SubscriptionAppButton';
import { Badge } from '../SubscriptionAppBadge';
import { Modal } from './components/Modal';
import { Calendar } from './components/Calendar';
import { ProductSwapModal, Product } from './components/ProductSwapModal';
export type BulkActionType = 'reschedule' | 'swap' | 'status' | 'preview' | null;
export interface BulkActionsManagerProps {
  selectedIds: string[];
  onClearSelection: () => void;
  onActionComplete: () => void;
  className?: string;
}
// Mock wine product data
const mockProducts: Product[] = [{
  id: '1',
  name: 'Château Margaux 2015',
  description: 'Elegant Bordeaux with notes of blackcurrant and cedar',
  price: 450.0,
  billingPeriod: 'bottle',
  features: ['Bordeaux, France', 'Cabernet Sauvignon', 'Vintage 2015', '14.5% ABV'],
  category: 'red'
}, {
  id: '2',
  name: 'Cloudy Bay Sauvignon Blanc',
  description: 'Crisp New Zealand white with tropical fruit notes',
  price: 32.0,
  billingPeriod: 'bottle',
  features: ['Marlborough, NZ', 'Sauvignon Blanc', 'Vintage 2022', '13% ABV'],
  category: 'white'
}, {
  id: '3',
  name: 'Barolo Riserva DOCG',
  description: 'Bold Italian red with cherry and truffle complexity',
  price: 85.0,
  billingPeriod: 'bottle',
  features: ['Piedmont, Italy', 'Nebbiolo', 'Vintage 2016', '14% ABV'],
  category: 'red'
}, {
  id: '4',
  name: 'Moët & Chandon Impérial',
  description: 'Classic Champagne with apple and brioche notes',
  price: 55.0,
  billingPeriod: 'bottle',
  features: ['Champagne, France', 'Chardonnay Blend', '12% ABV'],
  category: 'sparkling'
}, {
  id: '5',
  name: 'Caymus Cabernet Sauvignon',
  description: 'Rich Napa Valley red with dark fruit and vanilla',
  price: 90.0,
  billingPeriod: 'bottle',
  features: ['Napa Valley, CA', 'Cabernet Sauvignon', 'Vintage 2020', '14.8% ABV'],
  category: 'red'
}, {
  id: '6',
  name: 'Chablis Premier Cru',
  description: 'Mineral-driven white Burgundy with citrus notes',
  price: 48.0,
  billingPeriod: 'bottle',
  features: ['Burgundy, France', 'Chardonnay', 'Vintage 2021', '12.5% ABV'],
  category: 'white'
}];
export const BulkActionsManager = ({
  selectedIds,
  onClearSelection,
  onActionComplete,
  className = ''
}: BulkActionsManagerProps) => {
  const [currentAction, setCurrentAction] = useState<BulkActionType>(null);
  // Action States
  const [newDate, setNewDate] = useState<Date | undefined>(undefined);
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [replacementProduct, setReplacementProduct] = useState<string>('');
  const [newStatus, setNewStatus] = useState<string>('');
  const [notifySubscribers, setNotifySubscribers] = useState<boolean>(true);
  // Handlers
  const openAction = (action: BulkActionType) => {
    setCurrentAction(action);
    // Reset states
    setNewDate(undefined);
    setSelectedProduct('');
    setReplacementProduct('');
    setNewStatus('');
    setNotifySubscribers(true);
  };
  const handleConfirmAction = () => {
    setCurrentAction('preview');
  };
  const handleProductSwapConfirm = () => {
    // Move to preview when both products are selected
    setCurrentAction('preview');
  };
  const handleFinalUpdate = () => {
    // Here you would make the API call
    console.log('Updating', selectedIds.length, 'subscriptions');
    setCurrentAction(null);
    onActionComplete();
  };
  // Render Helpers
  const renderRescheduleContent = () => <div className="flex flex-col items-center" data-id="element-380">
      <p className="text-[#475569] mb-6 text-center" data-id="element-381">
        Select a new next charge date and time for{' '}
        <strong data-id="element-382">{selectedIds.length}</strong> subscriptions.
      </p>
      <div className="border border-[#E2E8F0] rounded-lg p-4 bg-white shadow-sm" data-id="element-383">
        <Calendar value={newDate} onChange={setNewDate} includeTime={true} data-id="element-384" />
      </div>
      {newDate && <div className="mt-4 p-4 bg-[#E0F2FE] rounded-lg" data-id="element-385">
          <p className="text-[14px] text-[#475569] mb-1" data-id="element-386">
            New Charge Date & Time:
          </p>
          <p className="text-[16px] font-medium text-[#0F172A]" data-id="element-387">
            {newDate.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })}
          </p>
          <p className="text-[16px] font-medium text-[#0EA5E9]" data-id="element-388">
            {newDate.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        })}
          </p>
        </div>}
    </div>;
  const renderStatusContent = () => <div className="space-y-2" data-id="element-389">
      <p className="text-[#475569] mb-4" data-id="element-390">
        Change status for <strong data-id="element-391">{selectedIds.length}</strong> subscriptions.
      </p>
      {[{
      value: 'active',
      label: 'Active',
      icon: CheckCircle2,
      color: 'text-green-600'
    }, {
      value: 'paused',
      label: 'Paused',
      icon: Clock,
      color: 'text-orange-500'
    }, {
      value: 'cancelled',
      label: 'Cancelled',
      icon: XCircle,
      color: 'text-red-500'
    }].map(status => <button key={status.value} onClick={() => setNewStatus(status.value)} className={`w-full p-4 rounded-lg border text-left flex items-center gap-3 transition-all
            ${newStatus === status.value ? 'border-[#0EA5E9] bg-[#F0F9FF] ring-1 ring-[#0EA5E9]' : 'border-[#E2E8F0] hover:border-[#CBD5E1] hover:bg-[#F8FAFC]'}
          `} data-id="element-392">
          <status.icon className={status.color} size={20} data-id="element-393" />
          <span className="font-medium text-[#0F172A]" data-id="element-394">{status.label}</span>
        </button>)}
    </div>;
  const renderPreviewContent = () => {
    const selectedProductName = mockProducts.find(p => p.id === selectedProduct)?.name;
    const replacementProductName = mockProducts.find(p => p.id === replacementProduct)?.name;
    return <div className="space-y-6" data-id="element-395">
        <div className="bg-[#F0F9FF] border border-[#BAE6FD] rounded-lg p-4 flex gap-3" data-id="element-396">
          <AlertCircle className="text-[#0284C7] shrink-0" size={24} data-id="element-397" />
          <div data-id="element-398">
            <h4 className="font-semibold text-[#0C4A6E]" data-id="element-399">
              You are about to update {selectedIds.length} subscriptions
            </h4>
            <p className="text-[#0284C7] text-sm mt-1" data-id="element-400">
              Please review the changes below before confirming.
            </p>
          </div>
        </div>

        <div className="border border-[#E2E8F0] rounded-lg divide-y divide-[#E2E8F0]" data-id="element-401">
          <div className="p-4 flex justify-between items-center" data-id="element-402">
            <span className="text-[#64748B]" data-id="element-403">Action Type</span>
            <Badge variant="info" data-id="element-404">
              {newDate ? 'Reschedule' : selectedProduct && replacementProduct ? 'Swap Product' : 'Update Status'}
            </Badge>
          </div>
          <div className="p-4" data-id="element-405">
            <span className="text-[#64748B] block mb-2" data-id="element-406">
              {newDate ? 'New Charge Date' : selectedProduct && replacementProduct ? 'Product Change' : 'New Status'}
            </span>
            {newDate ? <span className="font-medium text-[#0F172A]" data-id="element-407">
                {newDate.toLocaleDateString()} at{' '}
                {newDate.toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            })}
              </span> : selectedProduct && replacementProduct ? <div className="flex items-center gap-2 text-[#0F172A]" data-id="element-408">
                <span className="font-medium" data-id="element-409">{selectedProductName}</span>
                <ArrowRight size={16} className="text-[#0EA5E9]" data-id="element-410" />
                <span className="font-medium" data-id="element-411">{replacementProductName}</span>
              </div> : <span className="font-medium text-[#0F172A]" data-id="element-412">{newStatus}</span>}
          </div>
          <div className="p-4 flex justify-between items-center" data-id="element-413">
            <div data-id="element-414">
              <span className="text-[#0F172A] font-medium block mb-1" data-id="element-415">
                Notify affected subscribers
              </span>
              <span className="text-[#64748B] text-sm" data-id="element-416">
                Send email notification about this change
              </span>
            </div>
            <button onClick={() => setNotifySubscribers(!notifySubscribers)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notifySubscribers ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-417">
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notifySubscribers ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-418" />
            </button>
          </div>
        </div>
      </div>;
  };
  if (selectedIds.length === 0) return null;
  return <>
      <div className={`bg-white border border-[#E2E8F0] rounded-lg p-4 flex items-center justify-between shadow-[0_1px_3px_rgba(15,23,42,0.08)] animate-in fade-in slide-in-from-top-2 duration-200 ${className}`} data-id="element-419">
        <div className="flex items-center gap-4" data-id="element-420">
          <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-421">
            {selectedIds.length}{' '}
            {selectedIds.length === 1 ? 'subscription' : 'subscriptions'}{' '}
            selected
          </span>
          <button onClick={onClearSelection} className="text-[13px] text-[#64748B] hover:text-[#0F172A] transition-colors duration-200" data-id="element-422">
            Clear selection
          </button>
        </div>

        <div className="flex items-center gap-3" data-id="element-423">
          <Button variant="outline" size="md" onClick={() => openAction('reschedule')} iconLeft={<CalendarIcon size={16} data-id="element-425" />} data-id="element-424">
            Reschedule
          </Button>
          <Button variant="outline" size="md" onClick={() => openAction('swap')} iconLeft={<RefreshCw size={16} data-id="element-427" />} data-id="element-426">
            Swap Product
          </Button>
          <Button variant="outline" size="md" onClick={() => openAction('status')} iconLeft={<Tag size={16} data-id="element-429" />} data-id="element-428">
            Update Status
          </Button>
        </div>
      </div>

      {/* Reschedule Modal */}
      <Modal isOpen={currentAction === 'reschedule'} onClose={() => setCurrentAction(null)} title="Reschedule Subscriptions" footer={<>
            <Button variant="ghost" onClick={() => setCurrentAction(null)} data-id="element-431">
              Cancel
            </Button>
            <Button disabled={!newDate} onClick={handleConfirmAction} data-id="element-432">
              Next
            </Button>
          </>} data-id="element-430">
        {renderRescheduleContent()}
      </Modal>

      {/* Swap Product Modal - 2-Step Process */}
      <ProductSwapModal isOpen={currentAction === 'swap'} onClose={() => setCurrentAction(null)} products={mockProducts} selectedProductId={selectedProduct} replacementProductId={replacementProduct} onSelectProduct={setSelectedProduct} onSelectReplacement={setReplacementProduct} onConfirm={handleProductSwapConfirm} data-id="element-433" />

      {/* Update Status Modal */}
      <Modal isOpen={currentAction === 'status'} onClose={() => setCurrentAction(null)} title="Update Status" footer={<>
            <Button variant="ghost" onClick={() => setCurrentAction(null)} data-id="element-435">
              Cancel
            </Button>
            <Button disabled={!newStatus} onClick={handleConfirmAction} data-id="element-436">
              Next
            </Button>
          </>} data-id="element-434">
        {renderStatusContent()}
      </Modal>

      {/* Preview Modal */}
      <Modal isOpen={currentAction === 'preview'} onClose={() => setCurrentAction(null)} title="Review your update" footer={<>
            <Button variant="ghost" onClick={() => setCurrentAction(null)} data-id="element-438">
              Back
            </Button>
            <Button onClick={handleFinalUpdate} data-id="element-439">Update</Button>
          </>} data-id="element-437">
        {renderPreviewContent()}
      </Modal>
    </>;
};